import type { Schemas } from "@acervo/api-client";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { Search, ShieldCheck, UserPlus } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import {
  Empty,
  ErrorBox,
  Modal,
  PageHeader,
  SecretBox,
  Spinner,
} from "../../components/ui";
import { api, useSession } from "../../lib/api";
import { relative, roleLabel } from "../../lib/format";

const route = getRouteApi("/_app/console/usuarios");

export function UsersPage() {
  const { q } = route.useSearch();
  const navigate = useNavigate();
  const [text, setText] = useState(q ?? "");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      const v = text.trim() || undefined;
      if (v !== q)
        void navigate({
          to: "/console/usuarios",
          search: { q: v },
          replace: true,
        });
    }, 300);
    return () => clearTimeout(t);
  }, [text, q, navigate]);

  const list = useInfiniteQuery({
    queryKey: ["admin", "users", q],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.unwrap(
        api.client.GET("/v1/admin/users", {
          params: { query: { q, cursor: pageParam, limit: 50 } },
        }),
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  return (
    <div>
      <PageHeader
        title="Usuários"
        actions={
          <button className="btn-primary" onClick={() => setCreating(true)}>
            <UserPlus className="h-4 w-4" /> Novo usuário
          </button>
        }
      />
      <div className="relative mb-4 max-w-sm">
        <Search
          className="pointer-events-none absolute top-2.5 left-3 h-4 w-4 text-muted"
          aria-hidden
        />
        <input
          type="search"
          className="field pl-9"
          placeholder="Nome ou e-mail"
          aria-label="Buscar usuários"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </div>
      {list.isPending ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <>
          <UserTable users={list.data.pages.flatMap((p) => p.items)} />
          {list.hasNextPage && (
            <div className="mt-4 flex justify-center">
              <button
                className="btn-ghost"
                disabled={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
              >
                Carregar mais
              </button>
            </div>
          )}
        </>
      )}
      {creating && <CreateUser onClose={() => setCreating(false)} />}
    </div>
  );
}

function UserTable({ users }: { users: Schemas["User"][] }) {
  if (users.length === 0) return <Empty title="Nenhum usuário encontrado" />;
  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted uppercase">
          <tr className="border-b border-line">
            <th className="px-4 py-2 font-medium">Nome</th>
            <th className="px-4 py-2 font-medium">Papel</th>
            <th className="px-4 py-2 font-medium">Situação</th>
            <th className="px-4 py-2 font-medium">Último acesso</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr
              key={u.id}
              className="border-b border-line/60 last:border-0 hover:bg-raised"
            >
              <td className="px-4 py-2">
                <Link
                  to="/console/usuarios/$userId"
                  params={{ userId: u.id }}
                  className="block"
                >
                  <span className="font-semibold">{u.display_name || "—"}</span>
                  <span className="block text-xs text-muted">{u.email}</span>
                </Link>
              </td>
              <td className="px-4 py-2">
                {roleLabel[u.role]}
                {u.mfa_enabled && (
                  <ShieldCheck
                    className="ml-1 inline h-3.5 w-3.5 text-ok"
                    aria-label="2FA ativo"
                  />
                )}
              </td>
              <td className="px-4 py-2">
                <StatusBadge user={u} />
              </td>
              <td className="px-4 py-2 text-muted">
                {relative(u.last_login_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function StatusBadge({ user }: { user: Schemas["User"] }) {
  if (user.status === "blocked")
    return <span className="badge bg-danger/15 text-danger">Bloqueado</span>;
  if (user.must_change_password)
    return (
      <span className="badge bg-accent/15 text-accent">Senha provisória</span>
    );
  return <span className="badge bg-ok/15 text-ok">Ativo</span>;
}

function CreateUser({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const me = useSession()!;
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<"student" | "author" | "family" | "admin">(
    "student",
  );
  const m = useMutation({
    mutationFn: () =>
      api.unwrap(
        api.client.POST("/v1/admin/users", {
          body: { email: email.trim(), display_name: name.trim(), role },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    m.mutate();
  };

  return (
    <Modal title="Novo usuário" onClose={onClose}>
      {m.isSuccess ? (
        <div className="space-y-4">
          <p className="text-sm">
            Conta de <strong>{m.data.user.email}</strong> criada. Envie a senha
            provisória por um canal seguro; ela não será mostrada de novo e a
            pessoa terá de trocá-la no primeiro acesso.
          </p>
          <SecretBox value={m.data.temporary_password} />
          <div className="flex justify-end gap-2">
            <Link
              to="/console/usuarios/$userId"
              params={{ userId: m.data.user.id }}
              className="btn-ghost"
              onClick={onClose}
            >
              Abrir ficha
            </Link>
            <button className="btn-primary" onClick={onClose}>
              Concluir
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label" htmlFor="n-email">
              E-mail
            </label>
            <input
              id="n-email"
              type="email"
              required
              autoFocus
              className="field"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="n-name">
              Nome
            </label>
            <input
              id="n-name"
              className="field"
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="n-role">
              Papel
            </label>
            <select
              id="n-role"
              className="field"
              value={role}
              onChange={(e) => setRole(e.target.value as typeof role)}
            >
              <option value="student">Aluno</option>
              <option value="family">Família (vê todo o seu acervo)</option>
              <option value="author">Autor</option>
              {me.user.role === "owner" && <option value="admin">Admin</option>}
            </select>
            {role === "admin" && (
              <p className="mt-1 text-xs text-muted">
                Admins precisam ativar o 2FA no primeiro acesso.
              </p>
            )}
          </div>
          {m.isError && <ErrorBox error={m.error} />}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={onClose}>
              Cancelar
            </button>
            <button className="btn-primary" disabled={m.isPending}>
              {m.isPending ? "Criando…" : "Criar conta"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
